/* ═══════════════════════════════════════════════════════════════
   ForecastingPage2 — Tranche 3 rebuild. Three modes over one dataset:
   Plan (sparse type-in-cell grid), View (read-only "my hours"), Meeting
   (Friday full-screen walkthrough + confirm). Replaces the old
   pages/ForecastingPage.tsx + pages/forecasting/* (deleted centrally
   once this passes verify — NOT in this commit).

   Own top-level nav item per D2 — see app.tsx's 'forecasting' view.
   ═══════════════════════════════════════════════════════════════ */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, LayoutGrid, Presentation } from 'lucide-react';
import type {
  TeamMember,
  Department,
  Project,
  DashboardView,
  Team,
  TeamAssignment,
  ProjectAssignment,
  HourForecast,
  ForecastConfirm,
} from '../../../shared/types';
import type { DeepLink } from '../../types/navigation';
import { hasPermission } from '../../../shared/permissions';
import { TeamService } from '../../services/teamService';
import { ProjectService } from '../../services/projectService';
import { DepartmentService } from '../../services/departmentService';
import { TimeService } from '../../services/timeService';
import { DashboardViewService } from '../../services/dashboardViewService';
import { ForecastConfirmService } from '../../services/forecastConfirmService';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import { rem } from '../../lib/rem';
import EmptyState from '../../components/EmptyState';
import type { ColumnFilters } from '../../components/DataTable';
import { FilterBar } from './FilterBar';
import { PlanGrid } from './PlanGrid';
import { ViewMode } from './ViewMode';
import { scopeToProject } from './buildPlanRows';
import { MeetingMode } from './MeetingMode';
import type {
  ForecastMode,
  HorizonWeeks,
  PersonLayout,
  Pivot,
  ProjectBreakdown,
  Scope,
} from './types';
import { addDays, daysBetween, mondayOf, todayIso } from './utils';

interface AuthUserLike {
  id: string;
  teamMemberId?: string;
  permissions: string[];
}

interface PlanSavedConfig {
  scope: Scope;
  filters: ColumnFilters;
  pivot: Pivot;
  personLayout: PersonLayout;
  projectBreakdown: ProjectBreakdown;
  horizonWeeks: HorizonWeeks;
}

const DEFAULT_SAVED_CONFIG: PlanSavedConfig = {
  scope: 'all',
  filters: {},
  pivot: 'person',
  personLayout: 'department',
  projectBreakdown: 'individual',
  horizonWeeks: 8,
};

const VIEW_SURFACE = 'forecasting.plan';

export default function ForecastingPage2({
  authUser,
  deepLink,
  onDeepLinkConsumed,
  toast,
  projectId,
  embedded,
}: {
  authUser: AuthUserLike | null | undefined;
  deepLink?: Extract<DeepLink, { view: 'forecasting' }> | null;
  onDeepLinkConsumed?: () => void;
  toast?: (msg: string, isError?: boolean) => void;
  /** T-PLUMB: renders this one project's forecasting only — used when a
   *  project page embeds ForecastingPage2 as a takeover. Forecasts and
   *  project assignments are scoped BEFORE the pivot builders run
   *  (see scopeToProject in buildPlanRows.ts) and the project picker is
   *  pinned to this project. `undefined` = firm-wide, unchanged. */
  projectId?: string;
  /** T-PLUMB: true when hosted inside another page's takeover rather than
   *  as its own top-level nav view. Suppresses deep-link consumption
   *  (the embedding page owns navigation, not a DeepLink). There is no
   *  separate page title/header row in this component to hide — the mode
   *  tabs bar is functional, not decorative chrome, so it stays. */
  embedded?: boolean;
}) {
  const currentUserId = authUser?.teamMemberId || '';
  const perms = authUser?.permissions || [];

  const canViewAll = hasPermission(perms, 'forecasting.view_all');
  const canViewTeam = hasPermission(perms, 'forecasting.view_team');
  const canViewOwn = hasPermission(perms, 'forecasting.view_own');
  const canEditAny = hasPermission(perms, 'forecasting.edit_any');
  const canEditTeam = hasPermission(perms, 'forecasting.edit_team');
  const canEditOwn = hasPermission(perms, 'forecasting.edit_own');
  const canBulk = hasPermission(perms, 'forecasting.bulk_assign');
  const canShareViews = hasPermission(perms, 'dashboards.share_views');
  const hasAnyEdit = canEditAny || canEditTeam || canEditOwn;
  const hasAnyView = canViewAll || canViewTeam || canViewOwn || hasAnyEdit;

  const [mode, setMode] = useState<ForecastMode>(hasAnyEdit ? 'plan' : 'view');

  // ─── Data ────────────────────────────────────────────────────────
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamAssignments, setTeamAssignments] = useState<TeamAssignment[]>([]);
  const [projectAssignments, setProjectAssignments] = useState<ProjectAssignment[]>([]);
  const [forecasts, setForecasts] = useState<HourForecast[]>([]);
  const [confirms, setConfirms] = useState<ForecastConfirm[]>([]);
  const [savedViews, setSavedViews] = useState<DashboardView[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // ─── Plan config (saved-view-able) ──────────────────────────────────
  const [config, setConfig] = useState<PlanSavedConfig>(DEFAULT_SAVED_CONFIG);
  const [searchQuery, setSearchQuery] = useState('');
  const [windowStart, setWindowStart] = useState<string>(() => mondayOf(todayIso()));
  const [meetingWeek, setMeetingWeek] = useState<string>(() => mondayOf(todayIso()));

  const todayMonday = useMemo(() => mondayOf(todayIso()), []);

  // ─── Initial load ────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const [memsR, deptsR, projsR, teamsR, teamAssignsR, projAssignsR, viewsR] =
          await Promise.allSettled([
            TeamService.list(),
            DepartmentService.list().catch(() => [] as Department[]),
            ProjectService.list(),
            TeamService.listTeams().catch(() => [] as Team[]),
            TeamService.listTeamAssignments().catch(() => [] as TeamAssignment[]),
            TeamService.listAssignments().catch(() => [] as ProjectAssignment[]),
            DashboardViewService.listForSurface(VIEW_SURFACE, currentUserId).catch(
              () => [] as DashboardView[]
            ),
          ]);
        if (memsR.status === 'fulfilled') setMembers(memsR.value);
        if (deptsR.status === 'fulfilled') setDepartments(deptsR.value);
        if (projsR.status === 'fulfilled') setProjects(projsR.value);
        if (teamsR.status === 'fulfilled') setTeams(teamsR.value);
        if (teamAssignsR.status === 'fulfilled') setTeamAssignments(teamAssignsR.value);
        if (projAssignsR.status === 'fulfilled') setProjectAssignments(projAssignsR.value);
        if (viewsR.status === 'fulfilled') {
          setSavedViews(viewsR.value);
          const def = viewsR.value.find((v) => v.userId === currentUserId && v.isDefault);
          if (def?.config) {
            setConfig({ ...DEFAULT_SAVED_CONFIG, ...(def.config as Partial<PlanSavedConfig>) });
            setActiveViewId(def.id);
          }
        }
        if (memsR.status === 'rejected' || projsR.status === 'rejected') {
          setLoadError(
            'Some forecasting data failed to load. Team members or projects may be incomplete.'
          );
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [currentUserId]);

  // ─── Forecast range load — union of what Plan / View / Meeting need ──
  const loadRange = useMemo(() => {
    const planStart = windowStart;
    const planEnd = addDays(windowStart, config.horizonWeeks * 7 - 1);
    const viewStart = todayMonday;
    const viewEnd = addDays(todayMonday, 8 * 7 + 6);
    const meetingStart = addDays(meetingWeek, -7);
    const meetingEnd = addDays(meetingWeek, 13);
    const starts = [planStart, viewStart, meetingStart].sort();
    const ends = [planEnd, viewEnd, meetingEnd].sort();
    return { start: starts[0], end: ends[ends.length - 1] };
  }, [windowStart, config.horizonWeeks, todayMonday, meetingWeek]);

  const [forecastsLoading, setForecastsLoading] = useState(false);
  const loadForecasts = useCallback(async () => {
    setForecastsLoading(true);
    try {
      const [fRows, cRows] = await Promise.all([
        TimeService.listForecastsForRange(loadRange.start, loadRange.end),
        ForecastConfirmService.listForRange(loadRange.start, loadRange.end).catch(
          () => [] as ForecastConfirm[]
        ),
      ]);
      setForecasts(fRows);
      setConfirms(cRows);
    } finally {
      setForecastsLoading(false);
    }
  }, [loadRange.start, loadRange.end]);

  useEffect(() => {
    loadForecasts();
  }, [loadForecasts]);

  useEffect(() => {
    const h = () => loadForecasts();
    window.addEventListener('specforma-sync-complete', h);
    return () => window.removeEventListener('specforma-sync-complete', h);
  }, [loadForecasts]);

  // ─── Deep link ───────────────────────────────────────────────────
  // Embedded instances don't own navigation — the embedding page does —
  // so they never consume a DeepLink meant for the top-level nav view.
  // Project Canvas R1 (T3b): a `forecast-week` drill still needs to land
  // the embedded instance on that week, so `weekStart` is applied even
  // when embedded (additive) — the project filter is already pinned via
  // the `projectId` prop, so that part of the branch still skips.
  useEffect(() => {
    if (!deepLink) return;
    if (deepLink.weekStart) setWindowStart(mondayOf(deepLink.weekStart));
    if (embedded) return;
    if (deepLink.projectId) {
      setConfig((c) => ({
        ...c,
        pivot: 'project',
        filters: { ...c.filters, project: { type: 'multiselect', values: [deepLink.projectId!] } },
      }));
    }
    onDeepLinkConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink, embedded]);

  // ─── Scope sets ──────────────────────────────────────────────────
  const myTeamMemberIds = useMemo<Set<string>>(() => {
    const ledTeams = new Set(
      teamAssignments
        .filter((a) => a.teamMemberId === currentUserId && a.teamRole === 'leader')
        .map((a) => a.teamId)
    );
    if (ledTeams.size === 0) return new Set();
    const ids = new Set<string>();
    for (const a of teamAssignments) if (ledTeams.has(a.teamId)) ids.add(a.teamMemberId);
    ids.add(currentUserId);
    return ids;
  }, [teamAssignments, currentUserId]);

  const myPmProjectIds = useMemo<Set<string>>(
    () =>
      new Set(
        projects
          .filter((p) => p.pmId === currentUserId || p.picId === currentUserId)
          .map((p) => p.id)
      ),
    [projects, currentUserId]
  );

  // T-PLUMB: embedded-in-a-project instances narrow forecasts + project
  // assignments to `projectId` BEFORE anything downstream (scope sets,
  // the pivot builders) sees them. Firm-wide (`projectId` undefined) is an
  // exact passthrough — see scopeToProject's own test for that guarantee.
  const { forecasts: scopedForecasts, projectAssignments: scopedProjectAssignments } = useMemo(
    () => scopeToProject({ forecasts, projectAssignments }, projectId),
    [forecasts, projectAssignments, projectId]
  );

  const myPmMemberIds = useMemo<Set<string>>(() => {
    if (myPmProjectIds.size === 0) return new Set();
    const ids = new Set<string>([currentUserId]);
    for (const a of scopedProjectAssignments)
      if (myPmProjectIds.has(a.projectId)) ids.add(a.teamMemberId);
    for (const f of scopedForecasts)
      if (f.projectId && myPmProjectIds.has(f.projectId)) ids.add(f.teamMemberId);
    return ids;
  }, [myPmProjectIds, scopedProjectAssignments, scopedForecasts, currentUserId]);

  const scopeOptions = useMemo(
    () => [
      { value: 'mine' as Scope, label: 'Mine', available: true },
      { value: 'team' as Scope, label: 'My team', available: myTeamMemberIds.size > 0 },
      { value: 'pm' as Scope, label: 'My projects', available: myPmProjectIds.size > 0 },
      { value: 'all' as Scope, label: 'All', available: canViewAll },
    ],
    [myTeamMemberIds, myPmProjectIds, canViewAll]
  );

  useEffect(() => {
    const activeOpt = scopeOptions.find((o) => o.value === config.scope);
    if (!activeOpt || !activeOpt.available) {
      const fallback =
        scopeOptions.find((o) => o.value === 'all' && o.available)?.value ||
        scopeOptions.find((o) => o.value === 'pm' && o.available)?.value ||
        scopeOptions.find((o) => o.value === 'team' && o.available)?.value ||
        ('mine' as Scope);
      if (fallback !== config.scope) setConfig((prev) => ({ ...prev, scope: fallback }));
    }
  }, [scopeOptions, config.scope]);

  // ─── Filtering ───────────────────────────────────────────────────
  function multiValues(key: string): string[] {
    const fv = config.filters[key];
    return fv && fv.type === 'multiselect' ? fv.values : [];
  }

  const scopedMembers = useMemo(() => {
    let m = members.filter((x) => x.active);
    const allowedByPerm = (id: string): boolean => {
      if (canViewAll) return true;
      if (id === currentUserId) return true;
      if (canViewTeam && myTeamMemberIds.has(id)) return true;
      if (myPmMemberIds.has(id)) return true;
      return false;
    };
    m = m.filter((x) => allowedByPerm(x.id));

    if (config.scope === 'mine') m = m.filter((x) => x.id === currentUserId);
    else if (config.scope === 'team') m = m.filter((x) => myTeamMemberIds.has(x.id));
    else if (config.scope === 'pm') m = m.filter((x) => myPmMemberIds.has(x.id));

    const deptIds = multiValues('department');
    if (deptIds.length > 0) m = m.filter((x) => x.departmentId && deptIds.includes(x.departmentId));
    const teamIds = multiValues('team');
    if (teamIds.length > 0) {
      const wanted = new Set(teamIds);
      const memberInWantedTeam = new Set<string>();
      for (const a of teamAssignments)
        if (wanted.has(a.teamId)) memberInWantedTeam.add(a.teamMemberId);
      m = m.filter((x) => memberInWantedTeam.has(x.id));
    }
    const personIds = multiValues('person');
    if (personIds.length > 0) {
      const wanted = new Set(personIds);
      m = m.filter((x) => wanted.has(x.id));
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    members,
    config,
    canViewAll,
    canViewTeam,
    currentUserId,
    myTeamMemberIds,
    myPmMemberIds,
    teamAssignments,
  ]);

  const search = searchQuery.trim().toLowerCase();
  const visibleMembers = useMemo(() => {
    if (!search) return scopedMembers;
    return scopedMembers.filter((m) => m.name.toLowerCase().includes(search));
  }, [scopedMembers, search]);

  const visibleProjects = useMemo(() => {
    // T-PLUMB: embedded instances pin the project picker to the one
    // project they're scoped to — the user-chosen "project" filter never
    // applies here (there's nothing else for it to show).
    if (projectId) return projects.filter((proj) => proj.id === projectId);
    const projectIds = multiValues('project');
    let p =
      projectIds.length === 0 ? projects : projects.filter((proj) => projectIds.includes(proj.id));
    if (search) {
      p = p.filter(
        (proj) =>
          proj.name.toLowerCase().includes(search) ||
          (proj.projectNumber || '').toLowerCase().includes(search)
      );
    }
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, config.filters, search, projectId]);

  // ─── Copy previous week ──────────────────────────────────────────
  const [copying, setCopying] = useState(false);
  async function copyPreviousWeek() {
    if (copying) return;
    setCopying(true);
    try {
      const prevStart = addDays(windowStart, -7);
      const prevEnd = addDays(prevStart, 6);
      const visibleIds = visibleMembers.map((m) => m.id);
      const src = await TimeService.listForecastsForRange(prevStart, prevEnd, visibleIds);
      const manual = src.filter((f) => f.sourceKind !== 'vacation' && f.projectId && f.dayDate);
      const queue = [...manual];
      async function worker() {
        while (queue.length) {
          const f = queue.shift();
          if (!f || !f.dayDate) return;
          const dayOffset = daysBetween(prevStart, f.dayDate);
          const targetDay = addDays(windowStart, dayOffset);
          await TimeService.setForecastDayCell({
            teamMemberId: f.teamMemberId,
            projectId: f.projectId,
            dayDate: targetDay,
            hours: Number(f.hours) || 0,
            notes: f.notes,
          });
        }
      }
      await Promise.all(Array.from({ length: 8 }, () => worker()));
      await loadForecasts();
    } catch (err) {
      toast?.(`Copy failed: ${err instanceof Error ? err.message : 'unknown error'}`, true);
    } finally {
      setCopying(false);
    }
  }

  // ─── Saved views ─────────────────────────────────────────────────
  const activeView = savedViews.find((v) => v.id === activeViewId) || null;
  const viewIsDirty = useMemo(() => {
    if (!activeView) return false;
    const saved = {
      ...DEFAULT_SAVED_CONFIG,
      ...((activeView.config as Partial<PlanSavedConfig>) || {}),
    };
    return JSON.stringify(saved) !== JSON.stringify(config);
  }, [activeView, config]);

  async function refreshViews() {
    const list = await DashboardViewService.listForSurface(VIEW_SURFACE, currentUserId);
    setSavedViews(list);
  }
  async function saveCurrentAsView(name: string) {
    const saved = await DashboardViewService.save({
      id: '',
      userId: currentUserId,
      surface: VIEW_SURFACE,
      name,
      config: { ...config },
      isDefault: false,
      shared: false,
    });
    await refreshViews();
    setActiveViewId(saved.id);
  }
  async function updateActiveView() {
    if (!activeView) return;
    await DashboardViewService.save({ ...activeView, config: { ...config } });
    await refreshViews();
  }
  async function deleteView(id: string) {
    await DashboardViewService.delete(id);
    if (activeViewId === id) setActiveViewId(null);
    await refreshViews();
  }
  async function setDefaultView(id: string) {
    await DashboardViewService.setDefault(id, VIEW_SURFACE, currentUserId);
    await refreshViews();
  }
  async function renameView(id: string, name: string) {
    const v = savedViews.find((x) => x.id === id);
    if (!v) return;
    await DashboardViewService.save({ ...v, name });
    await refreshViews();
  }
  async function toggleViewShare(id: string) {
    const v = savedViews.find((x) => x.id === id);
    if (!v) return;
    await DashboardViewService.save({ ...v, shared: !v.shared });
    await refreshViews();
  }
  function applyView(v: DashboardView) {
    setConfig({ ...DEFAULT_SAVED_CONFIG, ...((v.config as Partial<PlanSavedConfig>) || {}) });
    setActiveViewId(v.id);
  }

  // ─── Edit permission for a given member's cells ─────────────────
  const canEditCell = useCallback(
    (memberId: string): boolean => {
      if (canEditAny) return true;
      if (currentUserId && memberId === currentUserId) return canEditOwn;
      if (canEditTeam && myPmMemberIds.has(memberId)) return true;
      return false;
    },
    [canEditAny, canEditOwn, canEditTeam, currentUserId, myPmMemberIds]
  );
  const canConfirmMember = useCallback(
    (memberId: string): boolean => canEditCell(memberId),
    [canEditCell]
  );

  if (!hasAnyView) {
    return (
      <div style={{ padding: 'var(--space-5)' }}>
        <EmptyState
          title="No access to Forecasting"
          subtitle="Ask an admin for a forecasting.view_own, view_team, or view_all permission."
        />
      </div>
    );
  }

  if (loading && members.length === 0) {
    return (
      <div role="status" aria-label="Loading forecasting" style={{ padding: 'var(--space-5)' }}>
        <div
          style={{
            width: rem(180),
            height: rem(22),
            borderRadius: 'var(--radius-md)',
            background: 'var(--sa)',
            animation: 'pulse 1.4s ease-in-out infinite',
            marginBottom: rem(16),
          }}
        />
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            style={{
              height: rem(44),
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--bd)',
              background: 'var(--sf)',
              marginBottom: rem(8),
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: rem(12),
          padding: `${rem(10)} var(--space-4)`,
          borderBottom: '1px solid var(--bd)',
          flexShrink: 0,
        }}
      >
        <Tabs value={mode} onValueChange={(v) => setMode(v as ForecastMode)}>
          <TabsList>
            <TabsTrigger value="plan" className="flex items-center gap-1.5">
              <Icon icon={LayoutGrid} em={1.1818} /> Plan
            </TabsTrigger>
            <TabsTrigger value="view" className="flex items-center gap-1.5">
              <Icon icon={Eye} em={1.1818} /> My hours
            </TabsTrigger>
            {hasAnyEdit && (
              <TabsTrigger value="meeting" className="flex items-center gap-1.5">
                <Icon icon={Presentation} em={1.1818} /> Meeting
              </TabsTrigger>
            )}
          </TabsList>
        </Tabs>
        {mode !== 'view' && (
          <Button
            variant="link"
            size="sm"
            onClick={() => setMode('view')}
            style={{ marginLeft: 'auto' }}
          >
            <Icon icon={Eye} em={1.3} /> My hours
          </Button>
        )}
      </div>

      {loadError && (
        <div
          role="alert"
          style={{
            padding: `var(--space-2) ${rem(16)}`,
            fontSize: 'var(--font-size-base)',
            color: 'var(--color-danger)',
            background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)',
            borderBottom: '1px solid var(--bd)',
          }}
        >
          {loadError}
        </div>
      )}
      {forecastsLoading && (
        <div
          aria-live="polite"
          style={{
            height: rem(2),
            background: 'linear-gradient(90deg, transparent, var(--ac), transparent)',
            backgroundSize: '200% 100%',
          }}
        />
      )}

      {mode === 'plan' && (
        <>
          <FilterBar
            members={visibleMembers}
            teams={teams}
            departments={departments}
            projects={visibleProjects}
            filters={config.filters}
            onFiltersChange={(next) => setConfig((c) => ({ ...c, filters: next }))}
            scope={config.scope}
            onScopeChange={(s) => setConfig((c) => ({ ...c, scope: s }))}
            scopeOptions={scopeOptions}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            pivot={config.pivot}
            onPivotChange={(p) => setConfig((c) => ({ ...c, pivot: p }))}
            personLayout={config.personLayout}
            onPersonLayoutChange={(v) => setConfig((c) => ({ ...c, personLayout: v }))}
            projectBreakdown={config.projectBreakdown}
            onProjectBreakdownChange={(v) => setConfig((c) => ({ ...c, projectBreakdown: v }))}
            horizonWeeks={config.horizonWeeks}
            onHorizonWeeksChange={(n) => setConfig((c) => ({ ...c, horizonWeeks: n }))}
            canBulk={canBulk}
            onCopyPrevWeek={copyPreviousWeek}
            copying={copying}
            savedViews={savedViews}
            activeViewId={activeViewId}
            viewIsDirty={viewIsDirty}
            onApplyView={applyView}
            onSaveView={saveCurrentAsView}
            onUpdateView={updateActiveView}
            onDeleteView={deleteView}
            onSetDefaultView={setDefaultView}
            onRenameView={renameView}
            onShareToggle={toggleViewShare}
            currentUserId={currentUserId}
            canShareViews={canShareViews}
          />
          <PlanGrid
            members={visibleMembers}
            projects={visibleProjects}
            allProjects={projectId ? visibleProjects : projects}
            forecasts={scopedForecasts}
            departments={departments}
            teams={teams}
            teamAssignments={teamAssignments}
            projectAssignments={scopedProjectAssignments}
            pivot={config.pivot}
            personLayout={config.personLayout}
            projectBreakdown={config.projectBreakdown}
            horizonWeeks={config.horizonWeeks}
            windowStart={windowStart}
            onWindowStartChange={setWindowStart}
            canEditCell={canEditCell}
            onRefetch={loadForecasts}
            toast={toast}
          />
        </>
      )}

      {mode === 'view' && (
        <ViewMode
          currentUserId={currentUserId}
          members={members}
          projects={projectId ? visibleProjects : projects}
          forecasts={scopedForecasts}
          departments={departments}
          teams={teams}
          teamAssignments={teamAssignments}
          projectAssignments={scopedProjectAssignments}
        />
      )}

      {mode === 'meeting' && hasAnyEdit && (
        <MeetingMode
          members={visibleMembers}
          projects={projectId ? visibleProjects : projects}
          forecasts={scopedForecasts}
          confirms={confirms}
          weekStart={meetingWeek}
          onWeekChange={setMeetingWeek}
          onExit={() => setMode('plan')}
          onConfirm={async (memberId) => {
            await ForecastConfirmService.confirmWeek(memberId, meetingWeek);
            await loadForecasts();
          }}
          canConfirm={canConfirmMember}
        />
      )}
    </div>
  );
}
